import { useState, useEffect } from 'react';
import { MessageSquare, Target, Users, BookOpen, ArrowRight, X } from 'lucide-react';

const TOUR_STEPS = [
  {
    target: 'quick-link-ai-guide',
    title: 'Power Guide',
    description: 'Your personal AI strategist powered by Claude. Ask about career pivots, AI tools, or your next moves.',
    icon: MessageSquare, position: 'bottom'
  },
  {
    target: 'quick-link-lifestyle',
    title: 'Pivot Journey',
    description: 'Walk through the Assess → Architect → Automate → Accelerate framework to build your personal Pivot Blueprint.',
    icon: Target, position: 'bottom'
  },
  {
    target: 'quick-link-community',
    title: 'Community',
    description: 'Connect with fellow members, share wins, ask questions, and find accountability partners.',
    icon: Users, position: 'bottom'
  },
  {
    target: 'quick-link-academy',
    title: 'Power Vault',
    description: 'Exclusive interviews, articles, and member spotlights you won\'t find anywhere else.',
    icon: BookOpen, position: 'bottom'
  }
];

export default function SiteTour({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [currentStep]);

  const updatePosition = () => {
    const step = TOUR_STEPS[currentStep];
    const el = document.querySelector(`[data-testid="${step.target}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const step = TOUR_STEPS[currentStep];
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-50" data-testid="site-tour-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Highlight cutout */}
      {targetRect && (
        <div
          className="absolute border-2 border-purple-400 rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.5)] z-10 pointer-events-none transition-all duration-300"
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16
          }}
        />
      )}

      {/* Tooltip */}
      {targetRect && (
        <div
          className="absolute z-20 w-80 animate-fadeIn"
          style={{
            top: targetRect.bottom + 20,
            left: Math.max(16, Math.min(targetRect.left, window.innerWidth - 336))
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon className="w-4 h-4 text-white" />
                <span className="text-white font-cormorant font-bold">{step.title}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-white/70 text-xs font-inter">{currentStep + 1}/{TOUR_STEPS.length}</span>
                <button onClick={onComplete} className="text-white/50 hover:text-white ml-1" data-testid="skip-tour-button">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-5">
              <p className="text-gray-600 font-inter text-sm leading-relaxed mb-4">{step.description}</p>
              <div className="flex items-center justify-between">
                <div className="flex space-x-1">
                  {TOUR_STEPS.map((_, i) => (
                    <div key={i} className={`w-2 h-2 rounded-full ${i === currentStep ? 'bg-purple-500' : i < currentStep ? 'bg-purple-200' : 'bg-gray-200'}`} />
                  ))}
                </div>
                <button onClick={handleNext}
                  className="flex items-center space-x-1 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-medium text-sm px-4 py-2 rounded-full hover:shadow-md transition-all"
                  data-testid="tour-next-button">
                  <span>{currentStep < TOUR_STEPS.length - 1 ? 'Next' : 'Done'}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
